import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShellRail } from '../../shell/AppShell';
import MarketNav from './MarketNav';
import MarketAvatar from './MarketAvatar';
import { searchAssets, fetchMovers, fetchPickAssets, fmtK, fmtPct } from './marketApi';
import './market.css';

const POSITIONS = ['All', 'QB', 'RB', 'WR', 'TE', 'Picks'];

const MarketBrowse = () => {
  const navigate = useNavigate();
  // Search/filter live in the URL (replacing, not pushing) so a browser-back
  // from an asset page lands on the same filtered view you left, not a reset one.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const position = searchParams.get('pos') || 'All';
  const setQuery = (q) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (q) next.set('q', q);
        else next.delete('q');
        return next;
      },
      { replace: true }
    );
  };
  const setPosition = (p) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p && p !== 'All') next.set('pos', p);
        else next.delete('pos');
        return next;
      },
      { replace: true }
    );
  };
  const [assets, setAssets] = useState(null);
  const [picks, setPicks] = useState(null);
  const [movers, setMovers] = useState(null);

  useEffect(() => {
    fetchMovers().then(setMovers).catch(() => setMovers({ risers: [], fallers: [] }));
  }, []);

  useEffect(() => {
    if (position !== 'Picks') return undefined;
    let active = true;
    fetchPickAssets()
      .then((r) => {
        if (active) setPicks(r.picks);
      })
      .catch(() => {
        if (active) setPicks([]);
      });
    return () => {
      active = false;
    };
  }, [position]);

  useEffect(() => {
    if (position === 'Picks') return undefined;
    let active = true;
    const t = setTimeout(() => {
      searchAssets({ query: query.trim() || undefined, position: position === 'All' ? undefined : position, limit: 250 })
        .then((r) => {
          if (active) setAssets(r.assets);
        })
        .catch(() => {
          if (active) setAssets([]);
        });
    }, query ? 250 : 0);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [query, position]);

  const open = (id) => navigate(`/market/p/${id}`);

  const tickRow = (entry) => (
    <button type="button" className="mk-tick" key={entry.player.id} onClick={() => open(entry.player.id)}>
      <MarketAvatar player={entry.player} size={24} />
      <span className="tnm">{entry.player.name}</span>
      <span className="tmeta">
        {entry.player.position} · {entry.player.team}
      </span>
      <span className={`tval ${entry.changePct >= 0 ? 'mk-up' : 'mk-dn'}`}>
        <span className="arrow">{entry.changePct >= 0 ? '▲' : '▼'}</span> {fmtPct(entry.changePct)}
      </span>
    </button>
  );

  return (
    <div className="ft-view">
      <Helmet>
        <title>Browse — The Market — Fantasy Taco</title>
      </Helmet>
      <MarketNav />

      <p className="rh-label">
        Browse
        <span>every dynasty asset, at real KTC value</span>
      </p>
      <div style={{ height: 14 }} />

      <input
        className="mk-search"
        placeholder="Search any player"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search players"
      />
      <div className="ft-pillrow" role="group" aria-label="Position filter" style={{ marginBottom: 22 }}>
        {POSITIONS.map((p) => (
          <button
            key={p}
            type="button"
            className={`ft-fpill${position === p ? ' on' : ''}`}
            onClick={() => setPosition(p)}
          >
            {p}
          </button>
        ))}
      </div>

      {!query && position !== 'Picks' && movers && (movers.risers.length > 0 || movers.fallers.length > 0) && (
        <div className="mk-cols" style={{ marginBottom: 26 }}>
          <div className="ft-card white" style={{ padding: '14px 18px' }}>
            <span className="ft-k" style={{ display: 'block', marginBottom: 4 }}>
              {position === 'All' ? 'Risers · last close' : `${position} risers · last close`}
            </span>
            {movers.risers
              .filter((r) => position === 'All' || r.player.position === position)
              .slice(0, 6)
              .map(tickRow)}
          </div>
          <div className="ft-card white" style={{ padding: '14px 18px' }}>
            <span className="ft-k" style={{ display: 'block', marginBottom: 4 }}>
              {position === 'All' ? 'Fallers · last close' : `${position} fallers · last close`}
            </span>
            {movers.fallers
              .filter((r) => position === 'All' || r.player.position === position)
              .slice(0, 6)
              .map(tickRow)}
          </div>
        </div>
      )}

      {position === 'Picks' ? (
        picks === null ? (
          <div className="ft-loading">Pricing the bond desk</div>
        ) : (
          <div className="ft-card white" style={{ padding: '6px 18px' }}>
            {picks
              .filter((p) => !query || p.label.toLowerCase().includes(query.toLowerCase()))
              .map((p) => (
                <button
                  type="button"
                  className="mk-tick"
                  key={p.label}
                  onClick={() => navigate(`/market/pick/${encodeURIComponent(p.label)}`)}
                >
                  <MarketAvatar pick={p} size={24} />
                  <span className="tnm">{p.label}</span>
                  <span className="tmeta">matures {p.maturity}</span>
                  <span className="tval">
                    <span className="mk-mono">{fmtK(p.value)}</span>{' '}
                    <span className={p.dayChangePct >= 0 ? 'mk-up' : 'mk-dn'}>
                      {Number.isFinite(p.dayChangePct) && <span className="arrow">{p.dayChangePct >= 0 ? '▲' : '▼'}</span>}{' '}
                      {fmtPct(p.dayChangePct)}
                    </span>
                  </span>
                </button>
              ))}
          </div>
        )
      ) : assets === null ? (
        <div className="ft-loading">Reading the tape</div>
      ) : (
        <div className="ft-card white" style={{ padding: '6px 18px' }}>
          {assets.length === 0 && (
            <p style={{ fontSize: 14, color: 'var(--ft-ink-3)', padding: '14px 0' }}>
              No one by that name has a market.
            </p>
          )}
          {assets.map((a) => (
            <button type="button" className="mk-tick" key={a.id} onClick={() => open(a.id)}>
              <MarketAvatar player={a} size={24} />
              <span className="tnm">{a.name}</span>
              <span className="tmeta">
                {a.position} · {a.team}
              </span>
              <span className="tval">
                <span className="mk-mono">{fmtK(a.value)}</span>{' '}
                <span className={a.dayChangePct >= 0 ? 'mk-up' : 'mk-dn'}>
                  {Number.isFinite(a.dayChangePct) && <span className="arrow">{a.dayChangePct >= 0 ? '▲' : '▼'}</span>}{' '}
                  {fmtPct(a.dayChangePct)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      <ShellRail>
        <div className="ft-railsec">
          <span className="ft-k">How it works</span>
          <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', margin: 0, lineHeight: 1.5 }}>
            Buy players you believe in. Short the ones you don't. Orders fill at the nightly close —
            being early is the whole game.
          </p>
        </div>
        <div className="ft-railsec">
          <span className="ft-k">Prices</span>
          <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', margin: 0, lineHeight: 1.5 }}>
            Real KeepTradeCut superflex values, synced nightly. Six years of daily history behind
            every player.
          </p>
        </div>
      </ShellRail>
    </div>
  );
};

export default MarketBrowse;
